import { create } from 'zustand'
import i18n from '@/i18n/config'
import { notificationsApi } from '@/api/notifications'
import { getErrorMessage } from '@/utils/error'
import type {
  AppNotification,
  NotificationListParams,
  NotificationStatus,
} from '@/types/notifications'

function notificationErrorMessage(error: unknown, fallbackKey: string): string {
  return getErrorMessage(error) || i18n.t(fallbackKey)
}

interface NotificationFilters {
  status: NotificationStatus
  event_key: string
}

interface NotificationsState {
  items: AppNotification[]
  latest: AppNotification[]
  toasts: AppNotification[]
  unreadCount: number
  page: number
  perPage: number
  total: number
  filters: NotificationFilters
  isLoading: boolean
  hasLoaded: boolean
  error: string | null
  bootstrap: () => Promise<void>
  loadPage: (params?: NotificationListParams) => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  receiveCreated: (notification: AppNotification, showToast?: boolean) => void
  receiveRead: (id: string, readAt?: string) => void
  receiveAllRead: (readAt?: string) => void
  dismissToast: (id: string) => void
  clearError: () => void
  reset: () => void
}

const initialState = {
  items: [] as AppNotification[],
  latest: [] as AppNotification[],
  toasts: [] as AppNotification[],
  unreadCount: 0,
  page: 1,
  perPage: 15,
  total: 0,
  filters: { status: 'all' as NotificationStatus, event_key: '' },
  isLoading: false,
  hasLoaded: false,
  error: null as string | null,
}

function upsert(items: AppNotification[], notification: AppNotification): AppNotification[] {
  return [notification, ...items.filter((item) => item.id !== notification.id)]
}

function matchesFilters(notification: AppNotification, filters: NotificationFilters): boolean {
  if (filters.event_key && notification.event_key !== filters.event_key) return false
  if (filters.status === 'unread' && notification.read_at) return false
  if (filters.status === 'read' && !notification.read_at) return false
  return true
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  ...initialState,

  bootstrap: async () => {
    try {
      const [latestPage, unreadPage] = await Promise.all([
        notificationsApi.list({ page: 1, per_page: 5, status: 'all' }),
        notificationsApi.list({ page: 1, per_page: 1, status: 'unread' }),
      ])
      set({
        latest: latestPage.data,
        unreadCount: unreadPage.meta.total,
      })
    } catch (error) {
      set({ error: notificationErrorMessage(error, 'notifications.loadError') })
    }
  },

  loadPage: async (params = {}) => {
    const state = get()
    const page = params.page ?? state.page
    const perPage = params.per_page ?? state.perPage
    const filters = {
      status: params.status ?? state.filters.status,
      event_key: params.event_key ?? state.filters.event_key,
    }
    set({ isLoading: true, error: null, filters })
    try {
      const response = await notificationsApi.list({
        page,
        per_page: perPage,
        status: filters.status,
        ...(filters.event_key ? { event_key: filters.event_key } : {}),
      })
      set({
        items: response.data,
        page: response.meta.current_page,
        perPage: response.meta.per_page,
        total: response.meta.total,
        hasLoaded: true,
      })
    } catch (error) {
      set({
        error: notificationErrorMessage(error, 'notifications.loadError'),
        hasLoaded: true,
      })
    } finally {
      set({ isLoading: false })
    }
  },

  markRead: async (id) => {
    const current = [...get().items, ...get().latest].find((item) => item.id === id)
    if (!current || current.read_at) return
    try {
      const notification = await notificationsApi.markRead(id)
      get().receiveRead(id, notification.read_at ?? new Date().toISOString())
    } catch (error) {
      set({ error: notificationErrorMessage(error, 'notifications.markReadError') })
      throw error
    }
  },

  markAllRead: async () => {
    try {
      await notificationsApi.markAllRead()
      get().receiveAllRead()
    } catch (error) {
      set({ error: notificationErrorMessage(error, 'notifications.markAllReadError') })
      throw error
    }
  },

  receiveCreated: (notification, showToast = true) => {
    set((state) => {
      const isNew = !state.latest.some((item) => item.id === notification.id)
      const insertIntoPage =
        state.page === 1 && matchesFilters(notification, state.filters)
      return {
        latest: upsert(state.latest, notification).slice(0, 5),
        items: insertIntoPage
          ? upsert(state.items, notification).slice(0, state.perPage)
          : state.items,
        total: insertIntoPage && isNew ? state.total + 1 : state.total,
        unreadCount:
          isNew && !notification.read_at ? state.unreadCount + 1 : state.unreadCount,
        toasts:
          showToast && isNew
            ? upsert(state.toasts, notification).slice(0, 4)
            : state.toasts,
      }
    })
  },

  receiveRead: (id, readAt = new Date().toISOString()) => {
    set((state) => {
      const wasUnread = [...state.items, ...state.latest].some(
        (item) => item.id === id && !item.read_at,
      )
      const patch = (items: AppNotification[]) =>
        items.map((item) => (item.id === id ? { ...item, read_at: readAt } : item))
      return {
        items:
          state.filters.status === 'unread'
            ? state.items.filter((item) => item.id !== id)
            : patch(state.items),
        latest: patch(state.latest),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        total:
          wasUnread && state.filters.status === 'unread'
            ? Math.max(0, state.total - 1)
            : state.total,
      }
    })
  },

  receiveAllRead: (readAt = new Date().toISOString()) => {
    set((state) => {
      const patch = (items: AppNotification[]) =>
        items.map((item) => (item.read_at ? item : { ...item, read_at: readAt }))
      return {
        items: state.filters.status === 'unread' ? [] : patch(state.items),
        latest: patch(state.latest),
        unreadCount: 0,
        total: state.filters.status === 'unread' ? 0 : state.total,
      }
    })
  },

  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) })),
  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}))
